import DeleteIcon from "@mui/icons-material/Delete";
import AlertIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Stack, Typography, useTheme } from "@mui/material";
import { groupBy, isEmpty } from "lodash";
import { Fragment, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";

import { Box } from "@/Components/v2/atoms/Box";
import { IconButton } from "@/Components/v2/atoms/Button";
import { Tooltip } from "@/Components/v2/atoms/ToolTip";
import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";
import { FormatCurrencyFunction } from "@/hooks/useConfig/types";

import { HistoricalSpendDTO } from "../../api/getHistoricalSpend";
import {
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
  StyledTableHeaderRow,
} from "../../components/StyledComponents";
import { bcpLayerKey, BCPNode } from "../../utils/bcp";
import { getBusinessUnitHierarchy } from "../../utils/formatBusinessUnitHeirachy";
import { useScenarioForms } from "../scenario-form/ScenarioFormProvider";
import { MediaConstraint } from "../scenario-form/types";
import { HistoricalSpends } from "../scenario-form/utils/logic";
import { AddOptimizerConstraintRow } from "./AddConstraintRows";
import { OptimizerConstraintRow } from "./ConstraintRows";
import { confirmDeleteBusinessUnit } from "./DeleteBusinessUnitConfirmationModal";
import { getHelperText } from "./utils/helperText";
import { channelsByBCPKey } from "./utils/logic";

const OptimizerConstraintsTable: React.FC<{
  historicalSpends: HistoricalSpends;
  form: UseFormReturn<{ mediaConstraints: MediaConstraint[] }>;
  disabled?: boolean;
  currencySymbol: string;
  formatCurrency: FormatCurrencyFunction;
  isConstraintsFromApiEnabled: boolean;
  historicalSpendsDTO: HistoricalSpendDTO[];
}> = ({
  historicalSpends,
  form,
  disabled = false,
  currencySymbol,
  formatCurrency,
  isConstraintsFromApiEnabled,
  historicalSpendsDTO,
}) => {
  const theme = useTheme();
  const [collapsedPanels, setCollapsedPanels] = useState<string[]>([]);

  const values = form.watch();
  const {
    formState: { errors },
  } = form;

  const constraintsByBCPKey = useMemo(
    () => groupBy(values.mediaConstraints, (constraint) => bcpLayerKey(constraint)),
    [values.mediaConstraints, errors]
  );

  const channelsByBCP = channelsByBCPKey(historicalSpendsDTO);

  const {
    handleMediaConstraintsRowChange,
    handleRemoveMediaConstraintsBusinessUnit,
    distinctBCP,
    spendConstraintsDTO,
  } = useScenarioForms();

  const { mapBcpName } = useDefaultNodeNameInfo();

  // Event Handlers
  const handleAccordionChange =
    (unitId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      if ((event.target as Element).closest("[data-delete-button]")) return;
      setCollapsedPanels((prev) =>
        isExpanded ? prev.filter((id) => id !== unitId) : [...prev, unitId]
      );
    };

  const handleDeleteConfirm = async (bcp: BCPNode) => {
    const confirmed = await confirmDeleteBusinessUnit({ theme });
    if (confirmed) {
      handleRemoveMediaConstraintsBusinessUnit(bcp);
    }
  };

  const removeConstraint = (index: number) => {
    handleMediaConstraintsRowChange({
      type: "remove",
      index,
    });
  };

  const addConstraint = (constraint: MediaConstraint) => {
    handleMediaConstraintsRowChange({
      type: "add",
      value: constraint,
    });
  };

  return (
    <>
      <Box>
        <Fragment>
          {Object.entries(constraintsByBCPKey).map(([bcpKey, constraints], index) => {
            const isExpanded = !collapsedPanels.includes(bcpKey);

            const selectedChannels = constraints.map((constraint) => constraint.channel);

            const availableChannels =
              channelsByBCP?.[bcpKey]?.map((channel) => ({
                ...channel,
                disabled: selectedChannels.includes(channel.key),
              })) ?? [];

            const bcp = distinctBCP?.get(bcpKey);
            if (!bcp) {
              console.error("BCP not found", bcpKey);
              return null;
            }

            const hasError = !isEmpty(errors.mediaConstraints?.[index]);

            return (
              <StyledAccordion
                key={bcpKey}
                expanded={isExpanded}
                onChange={handleAccordionChange(bcpKey)}
              >
                <StyledAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    flexDirection: "row-reverse",
                    "& .MuiAccordionSummary-content": {
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      margin: 0,
                    },
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      marginRight: 1,
                    },
                    ...(hasError &&
                      !isExpanded && {
                        borderColor: "error.main",
                        borderWidth: 1,
                        borderStyle: "solid",
                        color: "black",
                        backgroundColor: "error.light",
                      }),
                  }}
                >
                  <Stack sx={{ flex: 1 }} direction="row">
                    <Typography variant="subtitle1" sx={{ fontWeight: 400, fontSize: "1rem" }}>
                      {getBusinessUnitHierarchy(bcp, mapBcpName)}
                    </Typography>
                    {hasError && !isExpanded && (
                      <Tooltip
                        title="One or more of these constraints are invalid"
                        placement="top"
                        arrow
                      >
                        <AlertIcon sx={{ color: "error.main", ml: 1, cursor: "pointer" }} />
                      </Tooltip>
                    )}
                  </Stack>

                  {!disabled && (
                    <IconButton
                      size="small"
                      data-delete-button
                      onClick={(_event) => handleDeleteConfirm(bcp)}
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          color: "error.main",
                          backgroundColor: "error.light",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </StyledAccordionSummary>

                <StyledAccordionDetails>
                  <StyledTableHeaderRow sx={{ py: 0.5 }}>
                    <Box flex="1">
                      <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                        Media Channel
                      </Typography>
                    </Box>
                    <Box flex="1">
                      <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                        Min Spend
                      </Typography>
                    </Box>
                    <Box flex="1">
                      <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                        Max Spend
                      </Typography>
                    </Box>
                    <Box width="48px">
                      <span />
                    </Box>
                  </StyledTableHeaderRow>

                  {/* Constraint Rows */}
                  {constraints.map((constraint, constraintIndex) => {
                    const globalIndex = values.mediaConstraints.findIndex(
                      (c: MediaConstraint) =>
                        c.brand === constraint.brand &&
                        c.category === constraint.category &&
                        c.product === constraint.product &&
                        c.channel === constraint.channel
                    );

                    return (
                      <OptimizerConstraintRow
                        key={`${constraint.brand}-${constraint.category}-${constraint.product}-${constraint.channel}-${constraintIndex}`}
                        form={form}
                        constraint={constraint}
                        globalIndex={globalIndex}
                        availableChannels={availableChannels}
                        disabled={disabled}
                        helperText={getHelperText(
                          bcpKey,
                          constraint,
                          historicalSpends,
                          spendConstraintsDTO,
                          formatCurrency,
                          isConstraintsFromApiEnabled
                        )}
                        onRemove={() => removeConstraint(globalIndex)}
                        currencySymbol={currencySymbol}
                      />
                    );
                  })}

                  <AddOptimizerConstraintRow
                    disabled={disabled}
                    availableChannels={availableChannels ?? []}
                    onAddConstraint={(channel: string) => {
                      // Validate channel is not empty
                      if (!channel || channel.trim() === "") {
                        return;
                      }

                      const isDuplicate = values.mediaConstraints.some(
                        (constraint: MediaConstraint) =>
                          constraint.channel === channel &&
                          constraint.brand === bcp.brand &&
                          constraint.category === bcp.category &&
                          constraint.product === bcp.product
                      );

                      if (!isDuplicate) {
                        addConstraint({
                          channel,
                          brand: bcp.brand,
                          category: bcp.category,
                          product: bcp.product,
                          minValue: null!,
                          maxValue: null!,
                        });
                      }
                    }}
                  />
                </StyledAccordionDetails>
              </StyledAccordion>
            );
          })}
        </Fragment>
      </Box>
    </>
  );
};

export default OptimizerConstraintsTable;
